import React, { useCallback, useMemo } from 'react';
import {
  Accordion,
  IconBadgeStatus,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import _ from 'lodash';

import HouseholdMemberCard from './HouseholdMemberCard';
import { useApplicantForms } from './hooks';
import type { ApplicationDrawerProps } from './types';
import messages from './messages';
import useIntl from '../../hooks/useIntl';
import { isNotPetOrMinor, shouldBypassMoveInRequirements } from './utils';
import useDownloadAffordaPortalApplicationPDF from '../../hooks/data-modifying/useDownloadAffordaPortalApplicationPDF';
import ResetAffordaPortalButton from '../PersonalInformationModal/ResidentPortal/ResetAffordaPortalButton';

export default function HouseholdApplicationsDrawer({
  applicants: allApplicants,
  handleEditApplicationForm,
  propertyId,
  organizationId,
  isResident,
  applicationDecisionStatus,
  handleMultipleChecklistOptionsChange,
  affordaPortaV1,
  householdMembers = [],
  mainCustomerId,
  application,
  selectedProperty,
  onResetSuccess,
  currentUserId,
}: ApplicationDrawerProps) {
  const intl = useIntl();
  const sortApplicantsByStartDate = useCallback(
    (applicants) => {
      const applicantsWithStartDate = applicants.map((applicant) => {
        const householdMember = householdMembers.find(
          (member) => member.name === applicant.name,
        );
        if (householdMember) {
          applicant.startDate = householdMember.startDate;
        }
        return applicant;
      });

      const sortedApplicants = _.sortBy(applicantsWithStartDate, 'startDate');
      return sortedApplicants;
    },
    [householdMembers],
  );
  const hohApplicant = useMemo(() => {
    return allApplicants.length === 1
      ? allApplicants[0]
      : allApplicants.find(
          (applicant) =>
            applicant?.applicantCustomer?.customerId === mainCustomerId,
        );
  }, [allApplicants, mainCustomerId]);

  const applicants = useMemo(() => {
    const applicantsWithoutMinorsAndPets =
      allApplicants.filter(isNotPetOrMinor);
    const applicantsWithoutHOH = applicantsWithoutMinorsAndPets.filter(
      (applicant) => applicant.id !== hohApplicant?.id,
    );
    return [hohApplicant, ...sortApplicantsByStartDate(applicantsWithoutHOH)];
  }, [allApplicants, hohApplicant, sortApplicantsByStartDate]);

  const { data: formData, isLoading } = useApplicantForms({
    applicants,
    propertyId,
    organizationId,
  });

  const downloadAffordaPortalApplicationPDF =
    useDownloadAffordaPortalApplicationPDF();

  const allItemsComplete = useMemo(
    () =>
      applicants.every((applicant: any) => {
        if (isResident) return true;

        const applicationComplete =
          formData?.[applicant.id]?.isComplete ?? false;
        return applicant?.checklist?.approved && applicationComplete;
      }),
    [formData, applicants, isResident],
  );
  const bypassMoveInRequirement =
    shouldBypassMoveInRequirements(selectedProperty);

  return (
    <Accordion
      summary={
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: '100%' }}
          paddingRight={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <IconBadgeStatus
              variant={
                allItemsComplete || bypassMoveInRequirement
                  ? 'complete'
                  : 'incomplete'
              }
            />
            <Typography variant="bodyLarge">
              {intl.formatMessage(messages.title)}
            </Typography>
          </Stack>

          {/* Reset Application Button - positioned on the right side */}
          {affordaPortaV1 && application && selectedProperty && (
            <ResetAffordaPortalButton
              application={application}
              selectedProperty={selectedProperty}
              onResetSuccess={onResetSuccess}
              disabled={false}
              currentUserId={currentUserId}
            />
          )}
        </Stack>
      }
      sx={{ marginTop: 0, borderBottom: 'none' }}
    >
      <Stack
        direction="row"
        gap={2}
        sx={{
          overflow: 'auto',
          scrollbarWidth: 'thin',
          pb: 4,
          px: 1,
        }}
      >
        {applicants.map((applicant: any) => {
          const applicantChecklist = applicant.checklist;
          const applicantFormData = formData?.[applicant.id];
          const checklistApproved = isResident || applicantChecklist?.approved;
          const applicationComplete =
            isResident || applicantFormData?.isComplete;
          const checklistItems = applicantChecklist?.checklistItems || [];
          const isAdult = Boolean(applicant.applicantCustomer);
          const portalUserId =
            applicant.applicantCustomer?.customer?.pu?.id ?? null;

          return (
            <HouseholdMemberCard
              key={applicant.id}
              applicant={applicant}
              checklistItems={checklistItems}
              checklistApproved={checklistApproved}
              isLoading={isLoading}
              isAdult={isAdult}
              name={applicant.name}
              leaseSignerType={applicant.applicantType?.name}
              applicationComplete={applicationComplete}
              portalUserId={portalUserId}
              isResident={isResident}
              handleEditApplicationForm={() =>
                handleEditApplicationForm(applicant)
              }
              applicationDecisionStatuses={applicationDecisionStatus}
              handleMultipleChecklistOptionsChange={
                handleMultipleChecklistOptionsChange
              }
              downloadAffordaPortalApplicationPDF={
                downloadAffordaPortalApplicationPDF
              }
              affordaPortaV1={affordaPortaV1}
              propertyId={propertyId}
              organizationId={organizationId}
              selectedProperty={selectedProperty}
            />
          );
        })}
      </Stack>
    </Accordion>
  );
}
